import {
  createContext,
  useContext,
  type ReactNode,
  useSyncExternalStore,
  useCallback,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { trpc } from "@utils/trpc";
import type { DataSession } from "../data/core/session";
import type { AppRuntime } from "./createAppRuntime";
import { useStoreWithEqualityFn } from "zustand/traditional";
import type { AuthSession } from "./authSession";
import { SessionEndFailed } from "./SessionEndFailed";

import { DownloadsContext } from "../utils/downloadManager/hooks/useDownloads";

const RuntimeContext = createContext<AppRuntime | null>(null);
const EndSessionContext = createContext<AppRuntime["endSession"] | null>(null);
const DataContext = createContext<DataSession | null>(null);
const AuthSessionContext = createContext<AuthSession | null>(null);

export function RuntimeProvider({
  runtime,
  children,
}: {
  runtime: AppRuntime;
  children: ReactNode;
}) {
  const data = useSyncExternalStore(
    runtime.subscribe,
    () => (runtime.data.isCurrent() ? runtime.data : null),
    () => (runtime.data.isCurrent() ? runtime.data : null),
  );
  const failedEnd = useSyncExternalStore(
    runtime.subscribe,
    () => runtime.failedEnd,
    () => runtime.failedEnd,
  );
  if (!data)
    return failedEnd ? (
      <SessionEndFailed onRetry={runtime.retryEndSession} />
    ) : null;
  return (
    <RuntimeContext.Provider value={runtime}>
      <AuthSessionContext.Provider value={runtime.auth}>
        <DownloadsContext.Provider value={runtime.downloads}>
          <EndSessionContext.Provider value={runtime.endSession}>
            <DataContext.Provider key={data.generation} value={data}>
              <QueryClientProvider client={data.queryClient}>
                <trpc.Provider
                  client={data.trpcClient}
                  queryClient={data.queryClient}
                >
                  {children}
                </trpc.Provider>
              </QueryClientProvider>
            </DataContext.Provider>
          </EndSessionContext.Provider>
        </DownloadsContext.Provider>
      </AuthSessionContext.Provider>
    </RuntimeContext.Provider>
  );
}

export function useRuntime() {
  const runtime = useContext(RuntimeContext);
  if (!runtime) throw new Error("useRuntime requires RuntimeProvider");
  return runtime;
}

export function useData() {
  const data = useContext(DataContext);
  if (!data) throw new Error("useData requires RuntimeProvider");
  return data;
}

export function useApi() {
  return useData().api;
}

export function useAuthSession() {
  const auth = useContext(AuthSessionContext);
  if (!auth) throw new Error("useAuthSession requires RuntimeProvider");
  return auth;
}

export function useAuthStore<T>(
  selector: (state: ReturnType<AuthSession["store"]["getState"]>) => T,
) {
  return useStoreWithEqualityFn(useAuthSession().store, selector);
}

export function useDataAuth() {
  return useAuthStore((state) => state);
}

export function useEndSession() {
  const endSession = useContext(EndSessionContext);
  if (!endSession) throw new Error("useEndSession requires RuntimeProvider");
  const data = useData();
  return useCallback(
    (reason: Parameters<AppRuntime["endSession"]>[0]) =>
      endSession(reason, data.generation),
    [data, endSession],
  );
}

export function usePlayerEngine() {
  return useRuntime().player;
}
